/**
 * DateRangeFilters Component
 * Advanced date filtering with quick presets and custom range
 * Includes: Today, Last 7/30/90 Days, Custom Range
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 3 (AC-3 Advanced)
 */

import * as React from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { DateRangeInput } from './DateRangeInput';
import type { OrderFilters } from '../types/filter.types';

// ============================================
// INTERFACES
// ============================================

export interface DateRangeFiltersProps {
  dateRanges: OrderFilters['orderDateRange'];
  onDateRangeChange: (from: string | null, to: string | null) => void;
  className?: string;
}

interface QuickFilter {
  id: string;
  label: string;
  days: number | 'today';
}

// ============================================
// CONSTANTS
// ============================================

const QUICK_FILTERS: QuickFilter[] = [
  { id: 'today', label: 'Today', days: 'today' },
  { id: 'last-7', label: 'Last 7 Days', days: 7 },
  { id: 'last-30', label: 'Last 30 Days', days: 30 },
  { id: 'last-90', label: 'Last 90 Days', days: 90 },
];

// ============================================
// UTILITIES
// ============================================

/**
 * Get today's date in YYYY-MM-DD format
 */
function getTodayISO(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

/**
 * Get date N days ago in YYYY-MM-DD format
 */
function getDaysAgoISO(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
}

/**
 * Calculate date range for quick filter
 */
function getQuickFilterRange(days: number | 'today'): { from: string; to: string } {
  const today = getTodayISO();

  if (days === 'today') {
    return { from: today, to: today };
  }

  const from = getDaysAgoISO(days);
  return { from, to: today };
}

/**
 * Check if current date range matches a quick filter
 */
function getActiveQuickFilter(
  from: string | null,
  to: string | null
): string | null {
  if (!from || !to) return null;

  const today = getTodayISO();

  // Check each quick filter
  for (const filter of QUICK_FILTERS) {
    const range = getQuickFilterRange(filter.days);
    if (from === range.from && to === range.to) {
      return filter.id;
    }
  }

  return null;
}

/**
 * Format date range for display
 */
function formatDateRange(from: string | null, to: string | null): string {
  if (!from && !to) return '';

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  if (from && to) {
    return `${formatDate(from)} - ${formatDate(to)}`;
  }

  if (from) {
    return `From ${formatDate(from)}`;
  }

  return `Until ${formatDate(to)}`;
}

// ============================================
// COMPONENT
// ============================================

export const DateRangeFilters = React.memo(function DateRangeFilters({
  dateRanges,
  onDateRangeChange,
  className,
}: DateRangeFiltersProps) {
  const [isCustomMode, setIsCustomMode] = React.useState(false);

  const activeQuickFilter = getActiveQuickFilter(dateRanges.from, dateRanges.to);
  const hasCustomRange = dateRanges.from || dateRanges.to;

  // Handle quick filter click
  const handleQuickFilterClick = React.useCallback(
    (filterId: string) => {
      const filter = QUICK_FILTERS.find((f) => f.id === filterId);
      if (!filter) return;

      const range = getQuickFilterRange(filter.days);
      onDateRangeChange(range.from, range.to);
      setIsCustomMode(false);
    },
    [onDateRangeChange]
  );

  // Handle custom range button click
  const handleCustomRangeClick = React.useCallback(() => {
    setIsCustomMode(true);
  }, []);

  // Handle custom range change
  const handleCustomRangeChange = React.useCallback(
    (from: string | null, to: string | null) => {
      onDateRangeChange(from, to);
    },
    [onDateRangeChange]
  );

  // Handle clear
  const handleClear = React.useCallback(() => {
    onDateRangeChange(null, null);
    setIsCustomMode(false);
  }, [onDateRangeChange]);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      <label className="text-sm font-medium text-foreground flex items-center gap-2">
        <Calendar className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        Date Range
      </label>

      {/* Custom Date Range Input */}
      <div className="animate-in fade-in-50 duration-200">
        <DateRangeInput
          fromDate={dateRanges.from}
          toDate={dateRanges.to}
          onDateRangeChange={handleCustomRangeChange}
          label=""
        />
      </div>

      {/* Selected Range Badge */}
      {hasCustomRange && (
        <div className="flex items-center gap-2 pt-1">
          <Badge variant="secondary" className="text-xs font-normal">
            {formatDateRange(dateRanges.from, dateRanges.to)}
          </Badge>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear date filter"
          >
            ✕
          </button>
        </div>
      )}

      {/* Accessibility announcements */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {hasCustomRange
          ? `Date filter applied: ${formatDateRange(dateRanges.from, dateRanges.to)}`
          : 'No date filter applied'}
      </div>
    </div>
  );
});
